import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Outlet, useLoaderData, useLocation, useNavigate } from 'react-router-dom';
import { Button, EmptyState, Table, TableBody, TableCell, TableHeader, TableRow, cn } from '@immo/ui-kit';
import { ColumnsTrigger, type ColumnPreference } from '@immo/components';
import type { AdminListItem } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { RequirePermission } from '@/lib/auth/permissions';
import { useTranslations } from '@/i18n/use-translations';
import { ListLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import {
  CalendarIcon,
  KeyIcon,
  MailIcon,
  MembersIcon,
  PlusIcon,
  ShieldIcon,
  TagIcon,
  TextIcon,
} from '@/components/layout/icons';
import { FilterBar } from '@/components/filter-bar';
import { OperatorColumnHead } from '@/components/table/operator-column-head';
import type { OperatorColumn } from '@/components/table/operator-column';
import { loadOperatorColumnPreference, OPERATOR_COLUMN_KEYS } from '@/components/table/operator-column-store';
import { useOperatorTable } from '@/components/table/use-operator-table';
import { SEARCH_PARAM, useListParams } from '@/lib/list-view/use-list-params';

export const adminsLoaderParams = [] as const;

export type AdminsLoaderData = {
  readonly admins: ReadonlyArray<AdminListItem>;
  readonly columnPreference: ColumnPreference;
};

export async function adminsLoader(): Promise<AdminsLoaderData> {
  const [result, columnPreference] = await Promise.all([
    apiClient.adminAdmins.list(),
    loadOperatorColumnPreference(OPERATOR_COLUMN_KEYS.admins),
  ]);

  if (!result.ok) {
    throw new Response('Failed to load admins', { status: result.status });
  }

  return { admins: result.body.data, columnPreference };
}

function matches(admin: AdminListItem, query: string): boolean {
  if (query === '') return true;
  const haystack = [admin.fullName, admin.email, admin.id].join(' ').toLowerCase();

  return haystack.includes(query.toLowerCase());
}

// With every column visible the kit's icon heads need about 116px each before their label, so at
// 1024px the id column started ~160px past the content edge (measured +222px of horizontal
// overflow, 0 from 1280px). A `truncate` label made it worse, because `nowrap` raises each label's
// min-content. Below `xl` the five icon heads therefore show only the icon, and the head's `title`
// carries the label. Written as a whole literal so Tailwind emits it (BL-043, BL-065; IMM-1074.4 plan).
const LABEL_FROM_XL = 'hidden xl:inline';

function useAdminColumns(): ReadonlyArray<OperatorColumn<AdminListItem>> {
  const t = useTranslations('pages.admin.admins');
  const intl = useIntl();

  return useMemo(
    () => [
      {
        id: 'name',
        label: t('columns.name'),
        icon: TextIcon,
        headLabelClassName: LABEL_FROM_XL,
        cell: (admin) => admin.fullName,
        sortValue: (admin) => admin.fullName,
      },
      {
        id: 'email',
        label: t('columns.email'),
        icon: MailIcon,
        headLabelClassName: LABEL_FROM_XL,
        /*
          The e-mail is the other cell with no wrapping rule, so it carries the same
          floor for the same reason: an address has no spaces. Bounding five of six
          columns is not a budget while the sixth is free (BL-043) — without this the
          fix below holds only until a long enough address is listed. It gets `break-all`
          but not the id `<th>`'s `min-w-[144px]` floor: the AC ranks the id column's
          readability above the e-mail's, and only the id was measured to degrade past
          three lines without one — the e-mail is allowed to yield further.
        */
        cellClassName: 'break-all text-text-tertiary',
        cell: (admin) => admin.email,
        sortValue: (admin) => admin.email,
      },
      {
        id: 'status',
        label: t('columns.status'),
        icon: TagIcon,
        headClassName: 'w-[110px]',
        headLabelClassName: LABEL_FROM_XL,
        cell: (admin) => <StatusBadge status={admin.status} label={t(`status.${admin.status}`)} />,
        sortValue: (admin) => t(`status.${admin.status}`),
      },
      {
        id: 'createdAt',
        label: t('columns.created-at'),
        icon: CalendarIcon,
        headClassName: 'w-[150px]',
        headLabelClassName: LABEL_FROM_XL,
        cellClassName: 'text-text-tertiary',
        cell: (admin) => intl.formatDate(admin.createdAt, { dateStyle: 'short' }),
        sortValue: (admin) => Date.parse(admin.createdAt),
      },
      {
        id: 'createdBy',
        label: t('columns.created-by'),
        icon: MembersIcon,
        headLabelClassName: LABEL_FROM_XL,
        cellClassName: 'text-text-tertiary',
        cell: (admin) => admin.createdByFullName ?? t('created-by-system'),
        sortValue: (admin) => admin.createdByFullName,
      },
      {
        id: 'id',
        label: t('columns.id'),
        /*
          `min-w-[144px]` is a floor, not a width: in an auto-layout table a declared
          `width` is only a suggestion, and `min-width` is the side that binds (BL-065).
          It keeps the uuid from degrading past three lines once the column starts
          yielding; `max-w-xs` is the ceiling that leaves the 1440px rendering alone.
        */
        headClassName: 'min-w-[144px] max-w-xs',
        /*
          `break-all`, NOT `truncate`. `truncate` implies `white-space: nowrap`, which
          made this cell's min-content the whole 36-character uuid — a floor the table
          could not size under, so at 1024px the column started past the right edge of
          the content area and the id was sliced mid-glyph (IMM-834 QA-30 · UX-9). The
          ellipsis it promised never fired: a uuid is always narrower than `max-w-xs`.
          `break-words` would not help — only `break-all` lowers min-content.
        */
        cellClassName: 'max-w-xs break-all font-mono text-sm text-text-tertiary',
        cell: (admin) => admin.id,
        sortValue: (admin) => admin.id,
      },
    ],
    [t, intl],
  );
}

export function AdminsPage() {
  const t = useTranslations('pages.admin.admins');
  const tKeys = useTranslations('pages.admin.api-keys');
  const { admins, columnPreference } = useLoaderData() as AdminsLoaderData;
  const navigate = useNavigate();
  const location = useLocation();

  const { params, setParams } = useListParams();
  const query = params.get(SEARCH_PARAM) ?? '';
  const trimmedQuery = query.trim();
  const filtered = useMemo(() => admins.filter((a) => matches(a, trimmedQuery)), [admins, trimmedQuery]);

  // The whole of „has the operator narrowed the list?" — this page offers exactly
  // one filter. A second one added here has to be folded in, or the empty state
  // starts claiming the workspace is empty while it is merely filtered (BL-053).
  const searchActive = trimmedQuery !== '';

  const catalogue = useAdminColumns();
  const table = useOperatorTable({
    rows: filtered,
    preferenceKey: OPERATOR_COLUMN_KEYS.admins,
    catalogue,
    alwaysVisibleId: 'name',
    loadedPreference: columnPreference,
  });

  return (
    <>
      <PageTopbar
        leading={<ListLeading title={t('title')} count={filtered.length} icon={ShieldIcon} />}
        trailing={
          <div className="flex gap-2">
            {/*
              Issuing takes no operator argument: an admin API key binds to the
              ACTING operator and the request body carries no adminId, so this
              reads as "issue myself a key" wherever it is pressed. A per-row
              action would imply the row is the target, which is false.
            */}
            <RequirePermission permission="backoffice:identity:api-key-create">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => navigate({ pathname: '/admins/api-keys/new', search: location.search })}
              >
                <KeyIcon className="h-4 w-4" />
                {tKeys('issue-service-cta')}
              </Button>
            </RequirePermission>
            <RequirePermission permission="backoffice:admin:create">
              <Button size="sm" onClick={() => navigate({ pathname: '/admins/new', search: location.search })}>
                <PlusIcon className="h-4 w-4" />
                {t('create-cta')}
              </Button>
            </RequirePermission>
          </div>
        }
      />
      <FilterBar
        searchValue={query}
        searchPlaceholder={t('search-placeholder')}
        onSearchChange={(next, commit) => setParams({ [SEARCH_PARAM]: next }, { replace: commit.continuing })}
        trailing={<ColumnsTrigger {...table.columns.trigger} />}
      />
      {filtered.length === 0 ? (
        <div className="p-6">
          {searchActive ? (
            <EmptyState
              role="status"
              title={t('no-match-title')}
              description={t('no-match-description')}
              action={
                // Clearing `q` is the whole reset: `FilterSearch` follows the prop
                // when it changes from outside and drops any pending debounce on the
                // way (filter-bar.tsx:124-130), so the box clears with the list.
                <Button size="sm" variant="secondary" onClick={() => setParams({ [SEARCH_PARAM]: null })}>
                  {t('search-reset-cta')}
                </Button>
              }
            />
          ) : (
            <EmptyState title={t('empty')} />
          )}
        </div>
      ) : (
        <Table flush>
          <TableHeader>
            <TableRow {...table.columns.headerRowProps}>
              {table.columns.visibleColumns.map((column) => (
                <OperatorColumnHead key={column.id} column={column} table={table} />
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {table.sorted.map((admin) => (
              <TableRow key={admin.id} interactive onClick={() => navigate(`/admins/${admin.id}`)}>
                {table.columns.visibleColumns.map((column) => (
                  <TableCell
                    key={column.id}
                    className={cn(column.cellClassName, table.columns.bodyCellClassName(column.id))}
                    style={column.cellStyle}
                  >
                    {column.cell(admin)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Outlet />
    </>
  );
}

export function StatusBadge({ status, label }: { readonly status: AdminListItem['status']; readonly label: string }) {
  const classes = cn(
    'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
    status === 'active' ? 'bg-success-50 text-success-700' : 'bg-subtle text-text-tertiary',
  );

  return <span className={classes}>{label}</span>;
}
